import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { labelsApi } from '../lib/api';
import { usePrinter } from '../lib/usePrinter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Tag, Plus, Printer, Trash2, Edit2, X, Search, Clock, Bluetooth, BluetoothOff,
    BluetoothConnected, Loader2, WifiOff
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const ALLERGEN_LIST = [
    'Celery', 'Cereals containing gluten', 'Crustaceans', 'Eggs', 'Fish',
    'Lupin', 'Milk', 'Molluscs', 'Mustard', 'Nuts', 'Peanuts',
    'Sesame', 'Soybeans', 'Sulphur dioxide'
];

const CATEGORIES = ['Dairy', 'Meat', 'Poultry', 'Seafood', 'Vegetables', 'Fruit', 'Bakery', 'Sauces', 'Prepared', 'Other'];

export default function StorageLabels() {
    const { isManagerOrAdmin } = useAuth();
    const printer = usePrinter();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [search, setSearch] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [printData, setPrintData] = useState(null);
    const [printingId, setPrintingId] = useState(null);

    const [form, setForm] = useState({
        item_name: '', category: '', default_use_by_hours: 48,
        allergens: [], storage_instructions: ''
    });

    const loadTemplates = useCallback(() => {
        setLoading(true);
        const params = {};
        if (filterCategory) params.category = filterCategory;
        labelsApi.getTemplates(params)
            .then(({ data }) => setTemplates(data))
            .catch(() => toast.error('Failed to load templates'))
            .finally(() => setLoading(false));
    }, [filterCategory]);

    useEffect(() => { loadTemplates(); }, [loadTemplates]);

    const resetForm = () => {
        setForm({ item_name: '', category: '', default_use_by_hours: 48, allergens: [], storage_instructions: '' });
        setEditingId(null);
        setShowForm(false);
    };

    const handleSave = async () => {
        if (!form.item_name.trim()) { toast.error('Item name is required'); return; }
        try {
            const payload = {
                item_name: form.item_name,
                category: form.category || null,
                default_use_by_hours: parseInt(form.default_use_by_hours) || 48,
                allergens: form.allergens,
                storage_instructions: form.storage_instructions || null,
            };
            if (editingId) {
                await labelsApi.updateTemplate(editingId, payload);
                toast.success('Template updated');
            } else {
                await labelsApi.createTemplate(payload);
                toast.success('Template created');
            }
            resetForm();
            loadTemplates();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        }
    };

    const handleEdit = (t) => {
        setForm({
            item_name: t.item_name, category: t.category || '',
            default_use_by_hours: t.default_use_by_hours,
            allergens: t.allergens || [], storage_instructions: t.storage_instructions || ''
        });
        setEditingId(t.id);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this template?')) return;
        try { await labelsApi.deleteTemplate(id); toast.success('Deleted'); loadTemplates(); }
        catch { toast.error('Failed to delete'); }
    };

    const toggleAllergen = (a) => {
        setForm(prev => ({
            ...prev,
            allergens: prev.allergens.includes(a)
                ? prev.allergens.filter(x => x !== a)
                : [...prev.allergens, a]
        }));
    };

    const handleConnect = async () => {
        const result = await printer.connect();
        if (result.success) {
            toast.success(`Connected to ${result.deviceName || 'printer'}`);
        } else if (result.error !== 'Pairing cancelled') {
            toast.error(result.error);
        }
    };

    const handleDisconnect = async () => {
        await printer.disconnect();
        toast.success('Printer disconnected');
    };

    const handlePrint = async (templateId) => {
        setPrintingId(templateId);
        try {
            const { data } = await labelsApi.getPrintData(templateId);

            // Try BLE first if connected
            if (printer.connected) {
                const result = await printer.printViaBle(data);
                if (result.success) {
                    toast.success('Label printed!');
                } else {
                    toast.error(`BLE print failed: ${result.error}`);
                }
                // Never fall through to browser print when BLE was attempted
                return;
            }

            // Fallback: browser print dialog (only when no BLE connection)
            setPrintData(data);
            setTimeout(() => { window.print(); }, 300);
        } catch {
            toast.error('Failed to generate label');
        } finally {
            setPrintingId(null);
        }
    };

    const filtered = templates.filter(t =>
        t.item_name.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Layout>
            <div data-testid="storage-labels-page">
                {/* Header */}
                <div className="flex flex-col gap-3 mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
                                <Tag className="h-6 w-6 brand-icon" /> Storage Labels
                            </h1>
                            <p className="text-slate-400 text-sm mt-1">Create templates and print food storage labels</p>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={() => { resetForm(); setShowForm(true); }} className="brand-btn font-medium" data-testid="add-label-template-button">
                                <Plus className="h-4 w-4 mr-1" /> New Template
                            </Button>
                        </div>
                    </div>

                    {/* Printer Status Bar */}
                    <div className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border text-sm",
                        printer.connected
                            ? "bg-emerald-500/10 border-emerald-500/30"
                            : "bg-slate-800/50 border-slate-700"
                    )} data-testid="printer-status-bar">
                        {printer.connected ? (
                            <>
                                <BluetoothConnected className="h-4 w-4 text-emerald-400 shrink-0" />
                                <span className="text-emerald-400 font-medium flex-1 truncate">
                                    {printer.deviceName || 'NIIMBOT B1'} — Ready
                                </span>
                                <Button size="sm" variant="outline" onClick={handleDisconnect}
                                    className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs h-7 shrink-0" data-testid="disconnect-printer-button">
                                    <BluetoothOff className="h-3 w-3 mr-1" /> Disconnect
                                </Button>
                            </>
                        ) : printer.bleSupported ? (
                            <>
                                <Bluetooth className="h-4 w-4 text-slate-400 shrink-0" />
                                <span className="text-slate-400 flex-1">
                                    No printer connected — labels will use browser print
                                </span>
                                <Button size="sm" onClick={handleConnect} disabled={printer.connecting}
                                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7 shrink-0" data-testid="connect-printer-button">
                                    {printer.connecting ? (
                                        <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Connecting...</>
                                    ) : (
                                        <><Bluetooth className="h-3 w-3 mr-1" /> Connect NIIMBOT</>
                                    )}
                                </Button>
                            </>
                        ) : (
                            <>
                                <WifiOff className="h-4 w-4 text-slate-500 shrink-0" />
                                <span className="text-slate-500 flex-1">
                                    Web Bluetooth not available — using browser print
                                </span>
                            </>
                        )}
                    </div>
                </div>

                {/* Search and Filter */}
                <div className="flex flex-col sm:flex-row gap-2 mb-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items..."
                            className="pl-9 bg-slate-800 border-slate-700 text-slate-100" data-testid="search-labels-input" />
                    </div>
                    <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                        className="h-10 rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-slate-100 w-full sm:w-40" data-testid="filter-category">
                        <option value="">All Categories</option>
                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                {/* Form */}
                {showForm && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 mb-6" data-testid="label-template-form">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-slate-200">{editingId ? 'Edit Template' : 'New Label Template'}</h2>
                            <Button variant="ghost" size="icon" onClick={resetForm} className="text-slate-400 h-7 w-7"><X className="h-4 w-4" /></Button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-slate-300">Item Name *</Label>
                                <Input value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="e.g. Sliced Ham" data-testid="label-item-name" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Category</Label>
                                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                                    className="mt-1 w-full h-10 rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-slate-100" data-testid="label-category">
                                    <option value="">Select category</option>
                                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label className="text-slate-300">Use-By Period (hours)</Label>
                                <Input type="number" value={form.default_use_by_hours} onChange={(e) => setForm({ ...form, default_use_by_hours: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="48" data-testid="label-use-by-hours" />
                                <p className="text-[11px] text-slate-500 mt-1">{form.default_use_by_hours}h = {(form.default_use_by_hours / 24).toFixed(1)} days</p>
                            </div>
                            <div>
                                <Label className="text-slate-300">Storage Instructions</Label>
                                <Input value={form.storage_instructions} onChange={(e) => setForm({ ...form, storage_instructions: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="e.g. Keep refrigerated below 5°C" data-testid="label-storage-instructions" />
                            </div>
                        </div>

                        {/* Allergens */}
                        <div className="mt-4">
                            <Label className="text-slate-300 mb-2 block">Allergens (UK 14)</Label>
                            <div className="flex flex-wrap gap-1.5" data-testid="label-allergens-grid">
                                {ALLERGEN_LIST.map(a => (
                                    <button key={a} type="button" onClick={() => toggleAllergen(a)}
                                        className={cn(
                                            'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors',
                                            form.allergens.includes(a)
                                                ? 'bg-rose-500/20 text-rose-300 border-rose-500/40'
                                                : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600'
                                        )} data-testid={`allergen-toggle-${a.toLowerCase().replace(/\s/g, '-')}`}>
                                        {a}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex gap-2 mt-4">
                            <Button onClick={handleSave} className="brand-btn font-medium" data-testid="save-label-template-button">
                                {editingId ? 'Update Template' : 'Create Template'}
                            </Button>
                            <Button variant="outline" onClick={resetForm} className="border-slate-700 text-slate-300">Cancel</Button>
                        </div>
                    </div>
                )}

                {/* Templates Grid */}
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[1, 2, 3].map(i => <div key={i} className="h-40 bg-slate-900/60 rounded-xl animate-pulse" />)}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16 text-slate-500">
                        <Tag className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>No label templates yet</p>
                        <p className="text-xs mt-1">Create templates for items you frequently label</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {filtered.map((t) => (
                            <div key={t.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 flex flex-col" data-testid={`label-template-${t.id}`}>
                                <div className="flex items-start justify-between mb-2">
                                    <div>
                                        <h3 className="text-sm font-semibold text-slate-200">{t.item_name}</h3>
                                        {t.category && <span className="text-[11px] text-slate-500">{t.category}</span>}
                                    </div>
                                    <div className="flex gap-0.5">
                                        {isManagerOrAdmin && (
                                            <>
                                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-200"
                                                    onClick={() => handleEdit(t)} data-testid={`edit-template-${t.id}`}>
                                                    <Edit2 className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-400"
                                                    onClick={() => handleDelete(t.id)} data-testid={`delete-template-${t.id}`}>
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                                    <Clock className="h-3 w-3" />
                                    <span>Use by: {t.default_use_by_hours}h ({(t.default_use_by_hours / 24).toFixed(1)}d)</span>
                                </div>

                                {t.allergens?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-2">
                                        {t.allergens.map(a => (
                                            <span key={a} className="px-1.5 py-0.5 rounded text-[10px] bg-rose-500/15 text-rose-400 font-medium">{a}</span>
                                        ))}
                                    </div>
                                )}

                                {t.storage_instructions && (
                                    <p className="text-[11px] text-slate-500 mb-3">{t.storage_instructions}</p>
                                )}

                                <div className="mt-auto pt-2">
                                    <Button onClick={() => handlePrint(t.id)} disabled={!!printingId}
                                        className={cn(
                                            "w-full font-medium h-9 text-sm",
                                            printer.connected
                                                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                                                : "bg-slate-700 hover:bg-slate-600 text-slate-200"
                                        )}
                                        data-testid={`print-label-${t.id}`}>
                                        {printingId === t.id ? (
                                            <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Printing...</>
                                        ) : printer.connected ? (
                                            <><Printer className="h-4 w-4 mr-1.5" /> Print to NIIMBOT</>
                                        ) : (
                                            <><Printer className="h-4 w-4 mr-1.5" /> Print Label</>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Print label via portal (renders outside #root for clean print) */}
            {printData && createPortal(
                <div className="print-label">
                    <div className="print-label-header">
                        {printData.business_name && <div className="print-business-name">{printData.business_name}</div>}
                        <div className="print-item-name">{printData.item_name}</div>
                    </div>
                    <div className="print-label-body">
                        <div className="print-label-row">
                            <span className="print-label-key">OPENED:</span>
                            <span className="print-label-value">{printData.opened_at}</span>
                        </div>
                        <div className="print-label-row print-use-by">
                            <span className="print-label-key">USE BY:</span>
                            <span className="print-label-value">{printData.use_by}</span>
                        </div>
                        {printData.allergens?.length > 0 && (
                            <div className="print-allergens">
                                <span className="print-label-key">ALLERGENS:</span>
                                <span className="print-allergen-list">{printData.allergens.join(', ')}</span>
                            </div>
                        )}
                        {printData.storage_instructions && (
                            <div className="print-storage">{printData.storage_instructions}</div>
                        )}
                    </div>
                    <div className="print-label-footer">
                        <span>By: {printData.printed_by}</span>
                    </div>
                </div>,
                document.getElementById('print-label-root')
            )}
        </Layout>
    );
}
